import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserProfileModal from '../components/UserProfileModal';
import ThemeBackground from '../components/ThemeBackground';
import CreateRoomModal from '../components/CreateRoomModal';
import PublicRoomsModal from '../components/PublicRoomsModal';

const Home = () => {
  const [roomId, setRoomId] = useState('');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isCreateRoomModalOpen, setIsCreateRoomModalOpen] = useState(false);
  const [isPublicRoomsModalOpen, setIsPublicRoomsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const createRoom = () => {
    const newRoomId = Math.random().toString(36).substring(2, 8);
    navigate(`/room/${newRoomId}`, { replace: true });
  };

  const joinRoom = () => {
    if (roomId.trim()) {
      navigate(`/room/${roomId}`, { replace: true });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <ThemeBackground />
      <div className="absolute inset-0 z-10 flex items-center justify-center overflow-y-auto">
        <div className="max-w-5xl mx-auto p-6 h-full py-12">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-5xl font-bold mb-2 tracking-tight" style={{ color: 'var(--text-color)' }}>
                To<span style={{ color: 'var(--accent-color)' }}>Watch</span>
              </h1>
              <p style={{ color: 'var(--text-secondary)' }} className="text-lg">Ver videos en sincronía con amigos</p>
            </div>
            {user && (
              <div 
                className="flex items-center space-x-4 backdrop-blur-md rounded-full px-4 py-2 border transition-all cursor-pointer"
                style={{ 
                  backgroundColor: 'var(--card-bg)',
                  borderColor: 'var(--border-color)',
                  boxShadow: '0 0 10px var(--accent-color)'
                }}
                onClick={() => setIsProfileModalOpen(true)}
              >
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full overflow-hidden border" 
                      style={{ borderColor: 'var(--accent-color)' }}>
                    {profile?.avatar_url ? (
                      <img 
                        src={profile.avatar_url} 
                        alt={profile.username || 'Usuario'} 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center" 
                          style={{ backgroundColor: 'var(--button-bg)' }}>
                        <svg className="w-4 h-4" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <span className="text-sm hidden sm:block" style={{ color: 'var(--text-secondary)' }}>
                    {profile?.username || user.email || 'Usuario'}
                  </span>
                </div>
                <svg className="w-5 h-5" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Card 1: Crear sala */}
            <div className="themed-card backdrop-blur-md shadow-xl overflow-hidden transition-all duration-300 group">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                  style={{ backgroundColor: 'var(--button-bg)' }}></div>
              <div className="p-8 relative">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 rounded-full blur-2xl" 
                    style={{ backgroundColor: 'var(--button-bg)' }}></div>
                <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-32 h-32 rounded-full blur-2xl" 
                    style={{ backgroundColor: 'var(--button-bg)' }}></div>
                
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                      style={{ backgroundColor: 'var(--button-bg)' }}>
                    <svg className="w-6 h-6" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-semibold" style={{ color: 'var(--text-color)' }}>Crear nueva sala</h2>
                </div>
                
                <p style={{ color: 'var(--text-secondary)' }} className="mb-8">Crea una sala para compartir videos con tus amigos en tiempo real.</p>
                
                <div className="space-y-3">
                  <button
                    onClick={() => setIsCreateRoomModalOpen(true)}
                    className="themed-button w-full px-6 py-4 rounded-xl flex items-center justify-center space-x-2"
                  >
                    <span>Crear Sala</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={() => setIsPublicRoomsModalOpen(true)}
                    className="themed-button-secondary w-full px-6 py-3 rounded-xl flex items-center justify-center space-x-2 border transition-all hover:bg-opacity-80"
                    style={{ 
                      backgroundColor: 'var(--button-bg)', 
                      borderColor: 'var(--border-color)',
                      color: 'var(--button-text)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--button-hover)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--button-bg)';
                    }}
                  >
                    <span>Ver Salas Públicas</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Card 2: Unirse a sala */}
            <div className="themed-card backdrop-blur-md shadow-xl overflow-hidden transition-all duration-300 group">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                  style={{ backgroundColor: 'var(--button-bg)' }}></div>
              <div className="p-8 relative">
                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 rounded-full blur-2xl" 
                    style={{ backgroundColor: 'var(--button-bg)' }}></div>
                <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-32 h-32 rounded-full blur-2xl" 
                    style={{ backgroundColor: 'var(--button-bg)' }}></div>
                
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                      style={{ backgroundColor: 'var(--button-bg)' }}>
                    <svg className="w-6 h-6" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-semibold" style={{ color: 'var(--text-color)' }}>Unirse a una sala</h2>
                </div>
                
                <p style={{ color: 'var(--text-secondary)' }} className="mb-6">Ingresa el código de la sala a la que quieres unirte.</p>
                
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={roomId}
                      onChange={(e) => setRoomId(e.target.value)}
                      placeholder="Ingresa el código de la sala"
                      className="themed-input w-full rounded-xl px-5 py-4"
                    />
                    <div className="absolute inset-y-0 right-4 flex items-center">
                      <svg className="w-5 h-5" style={{ color: 'var(--accent-color)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                    </div>
                  </div>
                  
                  <button
                    onClick={joinRoom}
                    className="themed-button w-full px-6 py-4 rounded-xl flex items-center justify-center space-x-2"
                  >
                    <span>Unirse a Sala</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modales */}
      <UserProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />
      
      <CreateRoomModal
        isOpen={isCreateRoomModalOpen}
        onClose={() => setIsCreateRoomModalOpen(false)}
      />

      <PublicRoomsModal
        isOpen={isPublicRoomsModalOpen}
        onClose={() => setIsPublicRoomsModalOpen(false)}
      />
    </div>
  );
};

export default Home; 